import { createContext, useContext, useState, useEffect } from 'react';
import { 
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  updateProfile,
  sendPasswordResetEmail
} from 'firebase/auth';
import { doc, setDoc, getDoc } from 'firebase/firestore';
import { auth, db } from '../firebase/config';

const AuthContext = createContext();

export const useAuth = () => {
  return useContext(AuthContext);
};

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [loading, setLoading] = useState(true);

  // Sign up function
  const signup = async (email, password, displayName, role = 'customer') => {
    try {
      // Create the user in Firebase Auth
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      
      // Make sure we have a valid user object
      if (!userCredential || !userCredential.user) {
        throw new Error('Failed to create user account');
      }
      
      // Update profile with display name
      await updateProfile(userCredential.user, { displayName });
      
      // Create user document in Firestore
      const userDoc = {
        uid: userCredential.user.uid,
        email,
        displayName,
        photoURL: null,
        role,
        createdAt: new Date(),
        lastLogin: new Date(),
        // Add pending approval status for partner accounts
        ...(role === 'partner' && { pendingApproval: true })
      };
      
      // Determine which collection to use based on role
      let collectionName = 'users';
      if (role === 'partner') {
        collectionName = 'partners';
      } else if (role === 'admin') {
        collectionName = 'admins';
      }
      
      // Save the user document in the appropriate collection
      await setDoc(doc(db, collectionName, userCredential.user.uid), userDoc);
      
      // Set the user role
      setUserRole(role);
      
      return {
        user: userCredential.user
      };
    } catch (error) {
      console.error("Error in signup:", error);
      throw error;
    }
  };

  // Sign in function
  const login = async (email, password) => {
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      
      if (!userCredential.user) {
        throw new Error('Authentication failed');
      }

      // Determine user role and update last login time
      const role = await getUserRole(userCredential.user.uid);
      
      if (role) {
        let collectionName;
        
        if (role === 'admin') {
          collectionName = 'admins';
        } else if (role === 'partner') {
          collectionName = 'partners';
          
          // Check if partner is approved
          const partnerRef = doc(db, 'partners', userCredential.user.uid);
          const partnerDoc = await getDoc(partnerRef);
          
          if (partnerDoc.exists() && partnerDoc.data().pendingApproval === true) {
            // If partner is not approved, sign them out
            await signOut(auth);
            throw new Error('Your partner account is pending approval by an administrator.');
          }
        } else {
          collectionName = 'users';
        }
        
        const docRef = doc(db, collectionName, userCredential.user.uid);
        await setDoc(docRef, { lastLogin: new Date() }, { merge: true });
      }
      
      return userCredential.user;
    } catch (error) {
      throw error;
    }
  };

  // Logout function
  const logout = () => {
    return signOut(auth);
  };

  // Password reset function
  const resetPassword = (email) => {
    return sendPasswordResetEmail(auth, email);
  };

  // Update user profile
  const updateUserProfile = async (user, data) => {
    try {
      // Update the user's profile in Firebase Auth
      await updateProfile(user, {
        displayName: data.displayName || user.displayName,
        photoURL: data.photoURL || user.photoURL
      });

      // Get the user's role to determine which collection to update
      const role = await getUserRole(user.uid);
      
      if (role) {
        let collectionName;
        
        if (role === 'admin') {
          collectionName = 'admins';
        } else if (role === 'partner') {
          collectionName = 'partners';
        } else {
          collectionName = 'users';
        }

        // Update the user's document in Firestore
        const userRef = doc(db, collectionName, user.uid);
        await setDoc(userRef, data, { merge: true });
      }

      return true;
    } catch (error) {
      throw error;
    }
  };

  // Get user role
  const getUserRole = async (uid) => {
    try {
      if (!uid) {
        console.error('No uid provided to getUserRole');
        return null;
      }

      // Check admin collection first (highest privilege)
      const adminRef = doc(db, 'admins', uid);
      const adminDoc = await getDoc(adminRef);
      
      if (adminDoc.exists()) {
        setUserRole('admin');
        return 'admin';
      }
      
      // Check partner collection
      const partnerRef = doc(db, 'partners', uid);
      const partnerDoc = await getDoc(partnerRef);
      
      if (partnerDoc.exists()) {
        setUserRole('partner');
        return 'partner';
      }
      
      // Check user collection
      const userRef = doc(db, 'users', uid);
      const userDoc = await getDoc(userRef);
      
      if (userDoc.exists()) {
        // Check if role is explicitly defined as 'customer'
        if (userDoc.data().role === 'customer') {
          setUserRole('customer');
          return 'customer';
        } else {
          // For backward compatibility, default to 'customer' if the user exists in the users collection
          setUserRole('customer');
          return 'customer';
        }
      }
      
      // If user not found in any collection, create a default user document
      const currentAuthUser = auth.currentUser;
      if (currentAuthUser) {
        // Create a default user document
        const defaultUserDoc = {
          uid: currentAuthUser.uid,
          email: currentAuthUser.email,
          displayName: currentAuthUser.displayName || 'User',
          photoURL: currentAuthUser.photoURL,
          role: 'customer',
          createdAt: new Date(),
          lastLogin: new Date()
        };
        
        // Save to users collection
        await setDoc(doc(db, 'users', currentAuthUser.uid), defaultUserDoc);
        
        setUserRole('customer');
        return 'customer';
      }
      
      // If auth.currentUser is not available, set role to null
      setUserRole(null);
      console.warn('User not found in any collection and could not create default user document');
      return null;
    } catch (error) {
      console.error("Error determining user role:", error);
      setUserRole(null);
      return null;
    }
  };

  // Listen for auth state changes
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);
      
      if (user) {
        try {
          const role = await getUserRole(user.uid);
          // No need to handle errors here as getUserRole now returns null instead of throwing
        } catch (error) {
          console.error("Error in auth state change:", error);
        }
      } else {
        setUserRole(null);
      }
      
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  // Context value
  const value = {
    currentUser,
    userRole,
    signup,
    login,
    logout,
    resetPassword,
    updateUserProfile,
    getUserRole,
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}; 